import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, addDays, addMonths } from "date-fns";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Workforce, getDaysUntilEnd } from "@/types/workforce";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ExtendWorkforceModalProps {
	workforce: Workforce;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

export const ExtendWorkforceModal = ({
	workforce,
	open,
	onOpenChange,
	onSuccess
}: ExtendWorkforceModalProps) => {
	const { t } = useTranslation();
	const currentEndDate = workforce.expected_end_date || workforce.end_date;
	const [newEndDate, setNewEndDate] = useState<Date | undefined>(
		currentEndDate ? new Date(currentEndDate) : undefined
	);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const quickExtendOptions = [
		{ label: t("workforce.extend.oneMonth", "+1 Month"), days: 30 },
		{ label: t("workforce.extend.threeMonths", "+3 Months"), days: 90 },
		{ label: t("workforce.extend.sixMonths", "+6 Months"), days: 180 },
	];

	const handleQuickExtend = (days: number) => {
		const baseDate = newEndDate || new Date();
		setNewEndDate(addDays(baseDate, days));
	};

	const handleSubmit = async () => {
		if (!newEndDate) {
			toast({
				title: t("errors.error", "Error"),
				description: t("workforce.extend.selectDate", "Please select a new end date"),
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const { error } = await supabase
				.from('workforce')
				.update({
					expected_end_date: format(newEndDate, "yyyy-MM-dd"),
					updated_at: new Date().toISOString()
				})
				.eq('id', workforce.id);

			if (error) throw error;

			toast({
				title: t("workforce.extend.success", "Assignment Extended"),
				description: t("workforce.extend.successDesc", "The end date has been updated"),
			});

			onSuccess();
			onOpenChange(false);
		} catch (error) {
			toast({
				title: t("errors.error", "Error"),
				description: t("workforce.extend.error", "Failed to extend assignment"),
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const currentDaysRemaining = getDaysUntilEnd(currentEndDate);
	const newDaysRemaining = newEndDate ? getDaysUntilEnd(format(newEndDate, "yyyy-MM-dd")) : null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Clock className="h-5 w-5" />
						{t("workforce.extend.title", "Extend Assignment")}
					</DialogTitle>
					<DialogDescription>
						{t("workforce.extend.description", "Extend the expected end date for this assignment.")}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					{/* Current end date info */}
					<div className="p-3 rounded-sm bg-muted">
						<div className="text-sm text-muted-foreground">
							{t("workforce.extend.currentEndDate", "Current end date")}
						</div>
						<div className="font-medium">
							{currentEndDate ? format(new Date(currentEndDate), "PPP") : t("workforce.notSpecified", "Not specified")}
							{currentDaysRemaining !== null && currentDaysRemaining >= 0 && (
								<span className="text-muted-foreground ml-2">
									({currentDaysRemaining} {t("workforce.extend.daysRemaining", "days remaining")})
								</span>
							)}
						</div>
					</div>

					{/* Quick extend buttons */}
					<div>
						<Label className="text-sm mb-2 block">
							{t("workforce.extend.quickExtend", "Quick extend")}
						</Label>
						<div className="flex gap-2">
							{quickExtendOptions.map((option) => (
								<Button
									key={option.days}
									variant="outline"
									size="sm"
									onClick={() => handleQuickExtend(option.days)}
								>
									{option.label}
								</Button>
							))}
						</div>
					</div>

					{/* Date picker */}
					<div>
						<Label className="text-sm mb-2 block">
							{t("workforce.extend.newEndDate", "New end date")}
						</Label>
						<Popover>
							<PopoverTrigger asChild>
								<Button
									variant="outline"
									className={cn(
										"w-full justify-start text-left font-normal",
										!newEndDate && "text-muted-foreground"
									)}
								>
									<CalendarIcon className="mr-2 h-4 w-4" />
									{newEndDate ? format(newEndDate, "PPP") : t("workforce.extend.pickDate", "Pick a date")}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0" align="start">
								<Calendar
									mode="single"
									selected={newEndDate}
									onSelect={setNewEndDate}
									initialFocus
									disabled={(date) => date < new Date()}
								/>
							</PopoverContent>
						</Popover>
					</div>

					{/* Preview */}
					{newEndDate && newDaysRemaining !== null && (
						<div className="p-3 rounded-sm bg-muted border">
							<div className="text-sm text-muted-foreground">
								{t("workforce.extend.newDuration", "New duration")}
							</div>
							<div className="font-medium">
								{newDaysRemaining} {t("workforce.extend.daysRemaining", "days remaining")}
								{currentDaysRemaining !== null && currentDaysRemaining >= 0 && (
									<span className="text-muted-foreground ml-2">
										(+{newDaysRemaining - currentDaysRemaining} {t("workforce.extend.days", "days")})
									</span>
								)}
							</div>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting || !newEndDate}>
						{isSubmitting ? t("common.saving", "Saving...") : t("workforce.extend.confirm", "Extend Assignment")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
